import L from 'leaflet';

// NOTE: mapTilerLayers.js replaces the global `L.tileLayer` factory with a
// header-injecting, fetch-based variant for the Cambium tile server. Custom
// base layers point at arbitrary third-party servers, so they must NOT inherit
// that behaviour (it would attach the Cambium token to other hosts and force a
// CORS requirement). We therefore construct Leaflet's tile-layer classes
// directly, which the override leaves untouched.

// Common tile-pyramid options. `maxNativeZoom` is the deepest zoom the source
// actually has tiles for; Leaflet upscales the last native tiles beyond it
// rather than requesting tiles that don't exist (which return HTTP 400/404).
// Sources vary — GIBS GoogleMapsCompatible_Level8 stops at 8, many XYZ servers
// at 18/19 — so it is configurable per layer, defaulting to 18.
function tileOptions(layer, displayLabel) {
  const maxNativeZoom = Number(layer.maxNativeZoom);
  return {
    attribution: layer.attribution,
    displayLabel,
    maxNativeZoom:
      Number.isFinite(maxNativeZoom) && maxNativeZoom > 0 ? maxNativeZoom : 18,
    maxZoom: 21,
  };
}

function createWmsLayer(layer, displayLabel) {
  return new L.TileLayer.WMS(layer.url, {
    ...tileOptions(layer, displayLabel),
    format: layer.format || 'image/png',
    layers: layer.layers || '',
    transparent: false,
  });
}

// Build a Leaflet-templated tile URL for a WMTS source.
//
// Two encodings are supported:
//  - RESTful: the configured URL already carries template tokens
//    (Leaflet's {z}/{x}/{y} or the WMTS {TileMatrix}/{TileRow}/{TileCol}).
//    We normalise the WMTS tokens to Leaflet's and use the URL as-is.
//  - KVP: the configured URL is a plain GetTile endpoint, so we append the
//    standard WMTS GetTile query parameters with {z}/{y}/{x} placeholders that
//    Leaflet substitutes per tile.
function buildWmtsUrl(layer) {
  const url = layer.url;

  if (/\{z\}|\{tilematrix\}/i.test(url)) {
    return url
      .replace(/\{TileMatrix\}/gi, '{z}')
      .replace(/\{TileRow\}/gi, '{y}')
      .replace(/\{TileCol\}/gi, '{x}');
  }

  const params = {
    SERVICE: 'WMTS',
    REQUEST: 'GetTile',
    VERSION: '1.0.0',
    LAYER: layer.layers || '',
    STYLE: layer.style || 'default',
    TILEMATRIXSET: layer.tileMatrixSet || 'GoogleMapsCompatible',
    FORMAT: layer.format || 'image/png',
  };

  const query = Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');

  // TILEMATRIX/TILEROW/TILECOL are left as Leaflet placeholders so they are
  // not URL-encoded; Leaflet replaces them when each tile is requested.
  const placeholders = 'TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${query}&${placeholders}`;
}

function createWmtsLayer(layer, displayLabel) {
  return new L.TileLayer(buildWmtsUrl(layer), tileOptions(layer, displayLabel));
}

function createXyzLayer(layer, displayLabel) {
  return new L.TileLayer(layer.url, tileOptions(layer, displayLabel));
}

function createLayerDefinition(layer) {
  const displayLabel = layer?.name?.trim();
  if (!displayLabel || !layer?.url) {
    return null;
  }

  if (layer.type === 'wms') {
    return createWmsLayer(layer, displayLabel);
  }

  if (layer.type === 'wmts') {
    return createWmtsLayer(layer, displayLabel);
  }

  return createXyzLayer(layer, displayLabel);
}

export function createCustomBaseMaps(layers) {
  return (layers || []).reduce((acc, layer) => {
    const mapLayer = createLayerDefinition(layer);
    if (mapLayer) {
      acc[layer.name.trim()] = mapLayer;
    }
    return acc;
  }, {});
}
